import { createSlice } from '@reduxjs/toolkit';
import { getDefaultSidebarWidth } from '../utils/defaultSidebarWidth';

const likesSlice = createSlice({
    name: 'likes',
    initialState: {
        likedTracks: [],
        likedSet: {}, // "artist|||title" -> true for quick lookup
        // "artist|||title" -> true when the user turned the notification bell
        // on for that liked track. Separate from likedSet: liking alone never
        // subscribes to notifications (bell model, Aug 2026).
        notifySet: {},
        total: 0,
        // True once the like count has been fetched from the server at least
        // once this session. The "make it personal" feed nudge keys off this
        // so it never fires while the count is still its initial 0 (which read
        // as "no likes" and showed the nudge to users who actually have lots).
        loaded: false,
        page: 1,
        loading: false,
        sidebarOpen: false,
        sidebarWidth: getDefaultSidebarWidth(),
        error: null,
    },
    reducers: {
        setLikedTracks(state, action) {
            state.likedTracks = action.payload.data;
            state.total = action.payload.total;
            state.loaded = true;
        },
        appendLikedTracks(state, action) {
            state.likedTracks = [...state.likedTracks, ...action.payload.data];
            state.total = action.payload.total;
        },
        addLike(state, action) {
            const track = action.payload;
            // Add to the beginning of the list
            state.likedTracks = [track, ...state.likedTracks];
            state.total += 1;
            state.likedSet[`${track.artist}|||${track.title}`] = true;
        },
        removeLike(state, action) {
            const { artist, title } = action.payload;
            state.likedTracks = state.likedTracks.filter(
                t => !(t.artist === artist && t.title === title)
            );
            state.total = Math.max(0, state.total - 1);
            delete state.likedSet[`${artist}|||${title}`];
            delete state.notifySet[`${artist}|||${title}`];
        },
        setLikedSet(state, action) {
            state.likedSet = action.payload;
        },
        updateLikedSet(state, action) {
            // Merge new check results into existing set
            Object.assign(state.likedSet, action.payload);
        },
        updateNotifySet(state, action) {
            for (const [key, on] of Object.entries(action.payload)) {
                if (on) state.notifySet[key] = true;
                else delete state.notifySet[key];
            }
        },
        setPage(state, action) {
            state.page = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setLikesSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        toggleLikesSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setLikesSidebarWidth(state, action) {
            state.sidebarWidth = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        clearLikes(state) {
            state.likedTracks = [];
            state.likedSet = {};
            state.notifySet = {};
            state.total = 0;
            state.loaded = false;
            state.page = 1;
            state.loading = false;
            state.sidebarOpen = false;
            state.error = null;
        },
    },
});

export const {
    setLikedTracks,
    appendLikedTracks,
    addLike,
    removeLike,
    setLikedSet,
    updateLikedSet,
    updateNotifySet,
    setPage,
    setLoading,
    setLikesSidebarOpen,
    toggleLikesSidebar,
    setLikesSidebarWidth,
    setError,
    clearLikes,
} = likesSlice.actions;

export default likesSlice.reducer;
